import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

export type MessageStatus = 'sending' | 'sent' | 'failed';

type MessageSendResult = {
  success: boolean;
  message?: string;
  status?: number;
  data?: unknown;
  /** Present on some job-chat send responses when a chat is created. */
  jobChatId?: number;
};

type CacheMessage = {
  sending?: boolean;
  sent?: boolean;
  status?: MessageStatus;
};

type MessagesPage = {
  messages?: CacheMessage[];
  pagination?: { offset: number; max: number; count: number };
};

type FlatMessagesCache = {
  messages?: CacheMessage[];
};

type InfiniteMessagesCache = InfiniteData<MessagesPage>;

type MessagesQueryCache = FlatMessagesCache | InfiniteMessagesCache;

interface MessageMutationOptions<TParams, TOptimistic extends object = object> {
  queryKey: string;
  mutationFn: (params: TParams) => Promise<MessageSendResult>;
  getOptimisticMessage: (params: TParams) => TOptimistic;
  onSuccess?: (data: MessageSendResult, variables: TParams) => void;
  onError?: (error: Error, variables: TParams) => void;
}

function isInfiniteMessagesCache(data: unknown): data is InfiniteMessagesCache {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { pages?: unknown }).pages)
  );
}

function appendOptimisticMessage(
  old: MessagesQueryCache,
  optimisticMessage: CacheMessage
): MessagesQueryCache {
  if (isInfiniteMessagesCache(old)) {
    if (!old.pages?.length) {
      return old;
    }
    const pages = old.pages.map((page, index) =>
      index === 0
        ? {
            ...page,
            messages: [...(page.messages || []), optimisticMessage],
            // `has more older` compares page length against this count, so the
            // count has to grow with the message we just added to the page.
            pagination: page.pagination
              ? { ...page.pagination, count: page.pagination.count + 1 }
              : undefined,
          }
        : page
    );
    return { ...old, pages };
  }

  return {
    ...old,
    messages: [...(old.messages || []), optimisticMessage],
  };
}

function mapSendingMessages(
  old: MessagesQueryCache,
  mapper: (msg: CacheMessage) => CacheMessage
): MessagesQueryCache {
  if (isInfiniteMessagesCache(old)) {
    const pages = old.pages.map((page, index) =>
      index === 0
        ? {
            ...page,
            messages: (page.messages || []).map(mapper),
          }
        : page
    );
    return { ...old, pages };
  }

  return {
    ...old,
    messages: (old.messages || []).map(mapper),
  };
}

/**
 * This hook is used to send messages to the server and update the query cache
 * It is used in the JobMessageChat and DirectMessageChat components
 */
export const useMessageMutation = <
  TParams,
  TOptimistic extends object = object,
>(
  options: MessageMutationOptions<TParams, TOptimistic>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: options.mutationFn,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [options.queryKey] });
      const previousData = queryClient.getQueryData<MessagesQueryCache>([
        options.queryKey,
      ]);

      if (previousData) {
        const optimisticMessage: TOptimistic &
          CacheMessage & {
            id: string;
            timestamp: number;
            status: 'sending';
            sending: true;
            sent: false;
          } = {
          ...options.getOptimisticMessage(variables),
          id: uuidv4(),
          timestamp: Date.now(),
          status: 'sending',
          sending: true,
          sent: false,
        };

        queryClient.setQueryData(
          [options.queryKey],
          (old: MessagesQueryCache | undefined) =>
            old ? appendOptimisticMessage(old, optimisticMessage) : old
        );
      }

      return { previousData };
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([options.queryKey], context.previousData);
      }
      options.onError?.(error, variables);
    },
    onSuccess: (data, variables) => {
      if (data?.success) {
        const cached = queryClient.getQueryData<MessagesQueryCache>([
          options.queryKey,
        ]);

        queryClient.setQueryData(
          [options.queryKey],
          (old: MessagesQueryCache | undefined) =>
            old
              ? mapSendingMessages(old, (msg) =>
                  msg.sending ? { ...msg, sending: false, sent: true } : msg
                )
              : old
        );

        /**
         * Paged threads are fetched with offsets counted from the newest
         * message, so refetching every loaded page after a send shifts each
         * page window and drops already-loaded history. The optimistic message
         * we just confirmed already carries everything the thread renders, so
         * leave the paged cache alone and only refetch flat caches.
         */
        if (!cached || !isInfiniteMessagesCache(cached)) {
          queryClient.invalidateQueries({ queryKey: [options.queryKey] });
        }
      } else {
        queryClient.setQueryData(
          [options.queryKey],
          (old: MessagesQueryCache | undefined) =>
            old
              ? mapSendingMessages(old, (msg) =>
                  msg.sending ? { ...msg, sending: false, sent: false } : msg
                )
              : old
        );
      }
      options.onSuccess?.(data, variables);
    },
  });
};
